import { collapse, truncate } from './util';

const IMPLICIT_ROLES: Record<string, string> = {
  a: 'link',
  button: 'button',
  select: 'combobox',
  textarea: 'textbox',
  summary: 'summary',
  h1: 'heading',
  h2: 'heading',
  h3: 'heading',
  h4: 'heading',
  h5: 'heading',
  h6: 'heading',
  label: 'label',
  option: 'option',
};

const INPUT_TYPE_ROLES: Record<string, string> = {
  button: 'button',
  submit: 'button',
  reset: 'button',
  checkbox: 'checkbox',
  radio: 'radio',
  range: 'slider',
  number: 'spinbutton',
  search: 'searchbox',
  file: 'file',
};

export function roleOf(el: Element): string {
  const explicit = collapse(el.getAttribute('role'));
  if (explicit) return explicit.split(/\s+/)[0] ?? explicit;

  const tag = el.tagName.toLowerCase();
  if (tag === 'input') {
    const type = (el as HTMLInputElement).type || 'text';
    return INPUT_TYPE_ROLES[type] ?? 'textbox';
  }
  if (tag === 'a' && !el.hasAttribute('href')) return 'generic';
  return IMPLICIT_ROLES[tag] ?? 'generic';
}

function labelFor(el: Element): string {
  const id = el.getAttribute('id');
  if (id) {
    const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
    const label = el.ownerDocument.querySelector(`label[for="${escaped}"]`);
    if (label) return collapse(label.textContent);
  }
  const wrapping = el.closest('label');
  return wrapping ? collapse(wrapping.textContent) : '';
}

function ownText(el: Element): string {
  let out = '';
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      out += child.textContent ?? '';
    } else if (child instanceof Element) {
      if (child.matches('button, a[href], input, select, textarea')) continue;
      if (child.getAttribute('aria-hidden') === 'true') continue;
      out += ` ${child.textContent ?? ''}`;
    }
  }
  return collapse(out);
}

export function textOf(el: Element): string {
  const labelledBy = el.getAttribute('aria-labelledby');
  if (labelledBy) {
    const joined = labelledBy
      .split(/\s+/)
      .map((id) => el.ownerDocument.getElementById(id)?.textContent ?? '')
      .join(' ');
    if (collapse(joined)) return truncate(collapse(joined), 120);
  }

  const ariaLabel = collapse(el.getAttribute('aria-label'));
  if (ariaLabel) return truncate(ariaLabel, 120);

  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'select' || tag === 'textarea') {
    const label = labelFor(el);
    if (label) return truncate(label, 120);
    const input = el as HTMLInputElement;
    if (tag === 'input' && (input.type === 'submit' || input.type === 'button')) {
      const value = collapse(input.value);
      if (value) return truncate(value, 120);
    }
    const placeholder = collapse(el.getAttribute('placeholder'));
    if (placeholder) return truncate(placeholder, 120);
    const name = collapse(el.getAttribute('name'));
    if (name) return truncate(name, 120);
  }

  if (tag === 'img') {
    const alt = collapse(el.getAttribute('alt'));
    if (alt) return truncate(alt, 120);
  }

  const text = ownText(el);
  if (text) return truncate(text, 120);

  const title = collapse(el.getAttribute('title'));
  if (title) return truncate(title, 120);

  const svgTitle = collapse(el.querySelector('svg > title')?.textContent);
  return svgTitle ? truncate(svgTitle, 120) : '';
}

type VisibilityCapable = Element & {
  checkVisibility?: (options?: {
    checkOpacity?: boolean;
    checkVisibilityCSS?: boolean;
    contentVisibilityAuto?: boolean;
  }) => boolean;
};

export function isVisible(el: Element): boolean {
  if (el.hasAttribute('hidden')) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  if ((el as HTMLElement).inert) return false;
  if (el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'hidden') return false;

  const candidate = el as VisibilityCapable;
  if (typeof candidate.checkVisibility === 'function') {
    if (!candidate.checkVisibility({ checkVisibilityCSS: true, contentVisibilityAuto: true })) {
      return false;
    }
  } else {
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }

  /**
   * Only this element's own opacity counts. Asking the browser to walk ancestors
   * (checkOpacity) means a page that fades its content in on load reads as an
   * empty page for as long as the animation runs, and the map is built well
   * inside that window — the host we develop against fades in over 180ms.
   */
  if (Number(getComputedStyle(el).opacity) === 0) return false;

  const rect = el.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) return true;
  return el.matches('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
}
